import type { CarStanding, StandingsDoc } from './timing'
import { isInPits } from './timing.ts'

/*
The two cars either side, and whether the gap to them is opening or closing.

A pit wall reads this constantly and reads it fast, so the awkward parts are
worth stating. Which car is "ahead" is a question about the classification, not
about the road: the car a lap down sitting in front of you is not the car you
are racing, and a widget that said otherwise would be worse than one that said
nothing.

The vendor gives the gap to the car ahead on each car's own row. So our gap
forward is our own interval, and our gap backward is the interval belonging to
the car behind us — theirs is measured to us. Taking both from the same field
is what keeps the two halves consistent; deriving one of them from the gaps to
the leader would introduce a second definition that disagrees in the third
decimal.
*/

/** One neighbour, and how far away they are. */
export interface Neighbour {
  car: string
  /** The class the vendor scores them in, for colouring. */
  class?: string
  driver?: string
  team?: string
  position: number
  /**
   * The gap in milliseconds, or null when there is not one.
   *
   * Null is common and does not mean zero. The vendor reports a gap in time
   * only between cars on the same lap; once a lap separates them it reports
   * the laps and nothing else, because a time gap between cars a lap apart is
   * not a quantity anybody can use.
   */
  gapMs: number | null
  /** Whole laps between us, which take precedence over any time. */
  gapLaps: number
  /** Whether the timing system can see them stationary — see isInPits. */
  inPits: boolean
}

export interface Neighbours {
  /** Ahead in the classification, or null when we are leading. */
  ahead: Neighbour | null
  /** Behind in the classification, or null when we are last. */
  behind: Neighbour | null
  /** Our own position, or null when we are not in the standings at all. */
  position: number | null
}

/**
 * Who is either side of one car.
 *
 * Cars with no position are left out. A position of zero is how the vendor says
 * "not classified" — retired, or not yet away — and such a car is not one you
 * are racing for a place.
 */
export function neighboursOf(
  standings: StandingsDoc | null | undefined,
  car: string | null,
): Neighbours {
  const none: Neighbours = { ahead: null, behind: null, position: null }
  if (!car) return none

  const want = car.trim()
  const running = (standings?.cars ?? [])
    .filter((c) => (c.position ?? 0) > 0)
    .sort((a, b) => a.position - b.position)

  const i = running.findIndex((c) => c.number?.trim() === want)
  if (i < 0) return none

  return {
    position: running[i]?.position ?? null,
    // Our own interval is measured to the car in front of us.
    ahead: running[i - 1] ? toNeighbour(running[i - 1]!, running[i]!) : null,
    // Theirs is measured to us.
    behind: running[i + 1] ? toNeighbour(running[i + 1]!, running[i + 1]!) : null,
  }
}

function toNeighbour(who: CarStanding, gapFrom: CarStanding): Neighbour {
  const laps = gapFrom.intervalAheadLaps ?? 0
  return {
    car: who.number,
    class: who.class,
    driver: who.driver,
    team: who.team,
    position: who.position,
    // Laps win. The vendor sends null for the time once a lap separates two
    // cars, and inventing one from the fragments would be a number that looks
    // like a gap and is not.
    gapMs: laps > 0 ? null : (gapFrom.intervalAheadMs ?? null),
    gapLaps: laps,
    inPits: isInPits(who),
  }
}

// ------------------------------------------------------------------- trend

/** One reading of a gap. */
export interface GapSample {
  at: number
  ms: number
}

/**
 * How long a trace must span before it is worth reading.
 *
 * A trend measured over three seconds is measuring the timing system's
 * rounding. Twenty seconds is short enough to be current and long enough that
 * the answer is about the cars.
 */
export const minTrendMs = 20_000

/** How much history to keep. Two minutes is several laps of context at most circuits, and bounded. */
export const trendWindowMs = 120_000

/**
 * A change in the gap this large between two readings is not the cars moving.
 *
 * No two adjacent cars alter their gap by half a minute in a poll interval. It
 * is a car pitting out of sequence, a timing correction, or a glitch — and
 * averaging across it would report a closing rate nobody is achieving. The
 * trace restarts instead.
 */
export const gapJumpMs = 30_000

/**
 * Fold one reading into a trace, dropping what has aged out.
 *
 * Pure, and returns a new array, so the caller holds the trace and this stays
 * testable. The key is whatever identifies the pairing — change car, and the
 * history belongs to a different race and is discarded.
 */
export function recordGap(
  trace: readonly GapSample[],
  sample: GapSample,
  window = trendWindowMs,
): GapSample[] {
  const last = trace[trace.length - 1]
  if (last && (Math.abs(sample.ms - last.ms) > gapJumpMs || sample.at < last.at)) return [sample]
  // The same reading again, which the coalesced standings document produces
  // routinely. Handed back unchanged rather than copied, so a widget that
  // offers every render does not allocate on every render.
  if (last && sample.at === last.at) return trace as GapSample[]
  const kept = [...trace, sample].filter((s) => sample.at - s.at <= window)
  return kept.length > 0 ? kept : [sample]
}

export interface GapTrend {
  /** Change across the window. Negative means the gap is closing. */
  deltaMs: number
  /** What the change was measured over. */
  overMs: number
}

/** The change in a gap across the trace, or null while there is too little to say. */
export function gapTrend(trace: readonly GapSample[], minSpan = minTrendMs): GapTrend | null {
  const first = trace[0]
  const last = trace[trace.length - 1]
  if (!first || !last) return null
  const overMs = last.at - first.at
  if (overMs < minSpan) return null
  return { deltaMs: last.ms - first.ms, overMs }
}

/**
 * How a trend reads, in the words a pit wall already uses.
 *
 * Deliberately different words for the two directions. "Closing" is ambiguous
 * on a tile showing both neighbours at once — closing on them, or them on us? —
 * and the one thing this widget exists to make instant is which way round it
 * is.
 */
export type TrendVerdict = 'catching' | 'dropping back' | 'being caught' | 'pulling away' | 'holding'

/**
 * Below this the gap has not really moved.
 *
 * Gaps flicker by a tenth between polls because the two cars cross the loops at
 * different moments. A tile that alternated between "catching" and "dropping
 * back" on that would be read once and then ignored.
 */
export const trendDeadbandMs = 300

export function trendVerdict(trend: GapTrend | null, side: 'ahead' | 'behind'): TrendVerdict | null {
  if (!trend) return null
  if (Math.abs(trend.deltaMs) < trendDeadbandMs) return 'holding'
  const closing = trend.deltaMs < 0
  if (side === 'ahead') return closing ? 'catching' : 'dropping back'
  return closing ? 'being caught' : 'pulling away'
}
